import { fabric } from 'fabric';
import { useCallback, useRef } from 'react';
import { Point } from './Preview.types';
import { NEW_COMMENTZONE_OBJECT_NAME } from './previewUtils';

interface UseCommentZoneProps {
  canvas: fabric.Canvas;
  enabled: boolean;
  getCanvasPoint: (clientX: number, clientY: number) => Point;
  forcedStartPoint?: [number, number];
  onFinishDrawingZone: (x1: number, y1: number, x2?: number, y2?: number) => void;
}

export const useCommentZone = ({
  canvas,
  enabled,
  getCanvasPoint,
  forcedStartPoint,
  onFinishDrawingZone,
}: UseCommentZoneProps) => {
  const startPoint = useRef<[number, number] | null>(null);
  const commentZoneRect = useRef<fabric.Rect | null>(null);

  const handleCanvasMouseDown = useCallback(
    (event: fabric.IEvent<MouseEvent>) => {
      if (enabled) {
        const point = forcedStartPoint
          ? { x: forcedStartPoint[0], y: forcedStartPoint[1] }
          : getCanvasPoint(event.e.x, event.e.y);
        startPoint.current = [point.x, point.y];
      }
    },
    [enabled, forcedStartPoint, getCanvasPoint],
  );

  const handleCanvasMouseMove = useCallback(
    (event: fabric.IEvent<MouseEvent>) => {
      if (startPoint.current) {
        const point = getCanvasPoint(event.e.x, event.e.y);

        const width = Math.abs(point.x - startPoint.current[0]);
        const height = Math.abs(point.y - startPoint.current[1]);
        const left = Math.min(point.x, startPoint.current[0]);
        const top = Math.min(point.y, startPoint.current[1]);

        if (!commentZoneRect.current) {
          commentZoneRect.current = new fabric.Rect({
            left,
            top,
            width,
            height,
            fill: 'rgba(255, 230, 0, 0.2)',
            stroke: 'rgba(255, 230, 0, 0.35)',
            selectable: false,
            evented: false,
            hasBorders: false,
            hasControls: false,
            name: NEW_COMMENTZONE_OBJECT_NAME,
          });
          canvas.add(commentZoneRect.current);
          canvas.setActiveObject(commentZoneRect.current);
        } else {
          commentZoneRect.current.set({
            left,
            top,
            width,
            height,
          });
        }
        canvas.renderAll();
      }
    },
    [canvas, getCanvasPoint],
  );

  const handleCanvasMouseUp = useCallback(() => {
    if (commentZoneRect.current) {
      if (commentZoneRect.current.left && commentZoneRect.current.top) {
        const commentZone = {
          x1: commentZoneRect.current.left,
          y1: commentZoneRect.current.top,
          x2: commentZoneRect.current.width ? commentZoneRect.current.left + commentZoneRect.current.width : undefined,
          y2: commentZoneRect.current.height ? commentZoneRect.current.top + commentZoneRect.current.height : undefined,
        };
        canvas.remove(commentZoneRect.current);
        onFinishDrawingZone(commentZone.x1, commentZone.y1, commentZone.x2, commentZone.y2);
        startPoint.current = null;
      }
      canvas.remove(commentZoneRect.current);
    } else if (startPoint.current) {
      onFinishDrawingZone(startPoint.current[0], startPoint.current[1]);
      startPoint.current = null;
    }
    commentZoneRect.current = null;
  }, [canvas, onFinishDrawingZone]);

  return { handleCanvasMouseDown, handleCanvasMouseMove, handleCanvasMouseUp };
};
